// The chat console's side of opening a chat for another surface (a Triage
// row's "Investigate in chat", a case Investigate opens as a chat), written
// over the console's primitives so it can be exercised without the console.
// The console owns chats and agents; this keeps, per chat id, the options
// folded into that chat's agent constructor, and forgets them with the chat.
//
// Options live in this page only. A chat reloaded from storage asks
// `restore(chat)` for them, so a surface that keeps what its chats were
// opened on (lib/investigateChatKeep.js) gives a reloaded chat its kit back.
//
// `notify` runs a turn nobody typed: an investigation's coordinator hearing
// what its investigators found. The console runs it as a continuation, with
// no consent context of its own, so a live action in it fails closed the way
// every automated run's does.

import { resolveThinkingLevel } from "./modelCatalog.js";

// A chat's pinned depth goes through the resolution a chat's own does: on a
// model that cannot disable thinking, an "off" pin takes the model's default
// depth instead of the shallowest level the stream sends for an unset one.
function pinnedLevel(agent, level) {
    const model = agent?.state?.model;
    return resolveThinkingLevel(model?.provider, model?.id, level);
}

// A chat staged for steering holds its steering in the composer, one steer a
// line: a steer the person checks lands there as a line of its own, and what
// they edit or add there is what the work starts on.
const BULLET = /^\s*(?:[-*]|\d+[.)])\s+/;
const steerKey = (line) => String(line || "").replace(BULLET, "").replace(/\s+/g, " ").trim().toLowerCase();
export function steerLinesOf(input) {
    return String(input || "").split("\n").map((line) => line.replace(BULLET, "").replace(/\s+/g, " ").trim()).filter(Boolean);
}
export function inputHasSteer(input, text) {
    const want = steerKey(text);
    return Boolean(want) && String(input || "").split("\n").some((line) => steerKey(line) === want);
}
const withLine = (input, text) => {
    const base = String(input || "").replace(/\s+$/, "");
    return `${base ? `${base}\n` : ""}- ${text}`;
};
const withoutLine = (input, text) => String(input || "").split("\n").filter((line) => steerKey(line) !== steerKey(text)).join("\n");
// What a surface stages for steering, in the shape the console draws:
// { title, note, startLabel, emptyPrompt, groups: [{ id, label, gloss }],
// steers: [{ id, text, why, group, checked }] }.
function cleanSteering(steering) {
    if (!steering || typeof steering !== "object") return null;
    const steers = (Array.isArray(steering.steers) ? steering.steers : [])
        .map((s, i) => ({ id: String(s?.id || `s${i}`), text: String(s?.text || "").replace(/\s+/g, " ").trim(), why: s?.why ? String(s.why) : "", group: String(s?.group || ""), checked: s?.checked === true }))
        .filter((s) => s.text)
        .slice(0, 24);
    const groups = (Array.isArray(steering.groups) ? steering.groups : []).map((g) => ({ id: String(g?.id || ""), label: String(g?.label || ""), gloss: g?.gloss ? String(g.gloss) : "" })).filter((g) => g.id);
    return {
        title: String(steering.title || "Steer this"),
        note: steering.note ? String(steering.note) : "",
        startLabel: String(steering.startLabel || "Start"),
        emptyPrompt: String(steering.emptyPrompt || ""),
        groups,
        steers,
    };
}
const linesOf = (steering) => (steering?.steers || []).filter((s) => s.checked).reduce((input, s) => withLine(input, s.text), "");

// How long a turn told to a chat waits for the chat to be free (the person
// talking to it) before the teller is told to try again later.
export const NOTIFY_WAIT_MS = 5 * 60_000;
const NOTIFY_POLL_MS = 250;

export function createTriageChatHost({
    nextChatId,
    makeChat,
    addChat,
    updateChat,
    findChat,
    ensureAgentFor,
    activateChat,
    surface,
    showOverlay = () => {},
    hideOverlay = () => {},
    activateDock = () => {},
    focusComposer = () => {},
    sendPrompt = null,
    restore = () => null,
    runTurn = null,
    // The chat in front ({ chatId, serverChatId, busy }), and a chat's agent
    // built again with what it is built with now, its conversation kept:
    // what lets the chat in front take on a surface's tools (`equip`).
    current = () => null,
    rebuildAgent = null,
    notifyWaitMs = NOTIFY_WAIT_MS,
    now = Date.now,
    pause = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); }),
}) {
    const options = new Map();
    // What starts a chat staged for steering, by chat id: called with the
    // composer's text when the person starts it.
    const starts = new Map();

    // Notes read as the console's own system lines: what was handed to the
    // model, with the messages themselves behind the toggle.
    const NOTE_ID = "triage-note:";
    const noteItemsOf = (notes) => (Array.isArray(notes) ? notes : []).filter((n) => n && n.text).map((n, i) => ({
        kind: "system", id: `${NOTE_ID}${i}`, text: String(n.text), detail: n.detail ? String(n.detail) : null,
        snapshot: Array.isArray(n.snapshot) && n.snapshot.length ? n.snapshot : null,
    }));

    // Brings a chat on screen where the console says it belongs: the page's
    // chat column when there is one (activated when it is not showing the
    // chat, and the overlay put away so the two do not show at once), the
    // overlay otherwise.
    const surfaceChat = (id) => {
        const where = surface();
        if (where === "overlay") showOverlay();
        else {
            if (where === "activate") activateDock();
            hideOverlay();
        }
        activateChat(id);
    };

    const find = (ref) => findChat((c) => (
        (ref?.serverChatId && c.serverChatId === ref.serverChatId) || (ref?.chatId != null && c.id === ref.chatId)
    ));

    // A chat opened on something the person chose, staged for their
    // question: titled for it, a note saying what it opened on, the options
    // folded into its agent (built now, so it is ready when they ask), and
    // `context` sent ahead of their first prompt, once, with `starters`
    // offered as questions to put in the composer (or `startersPending` while
    // they are written, filled by `offer`) and `input` already there when a
    // question came with the click. Nothing runs until they ask. `ready`
    // settles with the agent, or null when it could not be built.
    const cleanStarters = (starters) => (Array.isArray(starters) ? starters : []).map((q) => String(q || "").trim()).filter(Boolean).slice(0, 3);
    // With `steering`, the chat opens staged for the person to steer it (see
    // `begin`): the steers drawn above the composer, the checked ones already
    // lines in it, and `onStart(text)` called with what the composer holds
    // when they start, returning the prompt that goes out (or nothing, for
    // the composer's own text).
    const stage = ({ title, notes = [], agentOptions, context = null, starters = [], startersPending = false, input = "", steering = null, onStart = null }) => {
        const id = nextChatId();
        const pendingContext = context?.text
            ? { text: String(context.text), label: String(context.label || ""), starters: cleanStarters(starters), startersPending: Boolean(startersPending) }
            : null;
        const cleaned = cleanSteering(steering);
        const chat = { ...makeChat(id), title: title || "Investigate", items: noteItemsOf(notes), pendingContext, steering: cleaned, input: String(input || linesOf(cleaned)) };
        const { thinkingLevel = null, ...buildOptions } = agentOptions || {};
        options.set(chat.id, buildOptions);
        if (cleaned && typeof onStart === "function") starts.set(chat.id, onStart);
        addChat(chat);
        surfaceChat(chat.id);
        focusComposer();
        const ready = (async () => {
            try {
                const agent = await ensureAgentFor(chat.id);
                if (agent && thinkingLevel) agent.state.thinkingLevel = pinnedLevel(agent, thinkingLevel);
                return agent || null;
            } catch {
                return null;
            }
        })();
        return { chatId: id, serverChatId: chat.serverChatId ?? null, ready };
    };

    // A staged chat whose first question goes out on its own, once the agent
    // is built: the click that opened it was the question. Sent through the
    // console's own submit, so the staged context rides ahead of it and the
    // turn is a person's turn like any other. `sent` settles when the turn
    // has been handed over, or with null when there was no agent to take it.
    const ask = ({ prompt, ...rest }) => {
        if (typeof sendPrompt !== "function") throw new Error("This chat console cannot send a question for another surface.");
        const staged = stage(rest);
        const sent = Promise.resolve(staged.ready).then((agent) => (agent ? sendPrompt(staged.chatId, String(prompt || "")) : null));
        return { ...staged, sent };
    };

    // The starters written for a staged chat, once they arrive. A chat whose
    // first question already went out, or that is gone, takes nothing.
    const offer = (ref, starters) => {
        const chat = find(ref);
        if (!chat) return false;
        const offered = cleanStarters(starters);
        updateChat(chat.id, (c) => (c.pendingContext
            ? { ...c, pendingContext: { ...c.pendingContext, starters: offered, startersPending: false } }
            : c));
        return true;
    };

    const open = (ref) => {
        const chat = find(ref);
        if (!chat) return false;
        surfaceChat(chat.id);
        return true;
    };

    // The chat in front takes on a surface's tools (`agentOptions`, as
    // `stage` takes them) rather than a new chat being opened for them: its
    // agent is built again with them, its conversation kept, and a reload
    // restores them as it restores a staged chat's. `notes` say so in its
    // transcript. Null when there is no chat in front, it is at work, or
    // the console cannot build its agent again; `ready` as `stage` has it.
    const equip = (ref, { agentOptions, notes = [] } = {}) => {
        const chat = find(ref);
        if (!chat || chat.busy || typeof rebuildAgent !== "function") return null;
        const { thinkingLevel: _pin, ...buildOptions } = agentOptions || {};
        options.set(chat.id, buildOptions);
        const at = now();
        const lines = noteItemsOf(notes).map((n, i) => ({ ...n, id: `${NOTE_ID}${at}:${i}` }));
        if (lines.length) updateChat(chat.id, (c) => ({ ...c, items: [...(c.items || []), ...lines] }));
        const ready = Promise.resolve().then(() => rebuildAgent(chat.id)).then(() => ensureAgentFor(chat.id)).catch(() => null);
        return { chatId: chat.id, serverChatId: chat.serverChatId ?? null, ready };
    };

    // The options a chat's agent is built with: those it was opened with in
    // this page, else what its surface restores for it after a reload.
    const optionsFor = (id) => {
        if (options.has(id)) return options.get(id);
        let restored = null;
        try { restored = restore(findChat((c) => c.id === id)) || null; } catch { restored = null; }
        if (!restored) return null;
        const { thinkingLevel: _pin, ...buildOptions } = restored;
        options.set(id, buildOptions);
        return buildOptions;
    };

    // A turn on what a surface tells the chat, once the chat is free: `text`
    // goes to the model as a notification, shown as a system line `label`
    // with the text behind its toggle. Resolves true once the turn ran, false
    // when the chat is gone, has no agent, or stayed busy past the wait, so
    // the teller can tell it again later. The chat need not be on screen.
    // `onCost` hears what each of the turn's replies cost.
    const notify = async (ref, { text, label = "", onCost = null } = {}) => {
        if (typeof runTurn !== "function" || !text) return false;
        const start = now();
        for (;;) {
            const chat = find(ref);
            if (!chat) return false;
            if (!chat.busy) {
                const agent = await ensureAgentFor(chat.id);
                if (!agent) return false;
                // Checked again after the build: a prompt may have gone out
                // meanwhile, and an agent takes one prompt at a time.
                const fresh = find(ref);
                if (!fresh) return false;
                if (!fresh.busy && !agent.state?.isStreaming) {
                    const message = { role: "notification", text: String(text), timestamp: now() };
                    updateChat(fresh.id, (c) => ({
                        ...c,
                        busy: true,
                        error: null,
                        items: [...(c.items || []), { kind: "system", id: `notify:${message.timestamp}`, text: String(label || "Update"), detail: null, snapshot: [message] }],
                    }));
                    const off = typeof onCost === "function" && typeof agent.subscribe === "function"
                        ? agent.subscribe((event) => {
                            if (event?.type !== "message_end" || event.message?.role !== "assistant") return;
                            const cost = Number(event.message?.usage?.cost?.total) || 0;
                            if (cost > 0) { try { onCost(cost); } catch { /* the teller's failure is its own */ } }
                        })
                        : null;
                    try {
                        await runTurn(fresh.id, agent, String(label || "notification"), [message]);
                        return true;
                    } catch {
                        updateChat(fresh.id, (c) => (c.busy ? { ...c, busy: false } : c));
                        return false;
                    } finally {
                        try { off?.(); } catch { /* unsubscribing cannot fail the turn */ }
                    }
                }
            }
            if (now() - start >= notifyWaitMs) return false;
            await pause(NOTIFY_POLL_MS);
        }
    };

    // An existing chat staged for steering again: the panel back above its
    // composer, the checked steers in it. False when the chat is gone or at
    // work.
    const steer = (ref, { steering, onStart = null } = {}) => {
        const chat = find(ref);
        const clean = cleanSteering(steering);
        if (!chat || chat.busy || !clean) return false;
        if (typeof onStart === "function") starts.set(chat.id, onStart);
        else starts.delete(chat.id);
        updateChat(chat.id, (c) => ({ ...c, steering: clean, input: linesOf(clean) || c.input }));
        surfaceChat(chat.id);
        focusComposer();
        return true;
    };

    // A check puts the steer in the composer as a line of its own, and takes
    // it out again; a line the person edited is theirs, not the steer's.
    const toggleSteer = (ref, steerId) => {
        const chat = find(ref);
        if (!chat?.steering) return false;
        updateChat(chat.id, (c) => {
            const s = c.steering?.steers.find((x) => x.id === steerId);
            if (!s) return c;
            return { ...c, input: inputHasSteer(c.input, s.text) ? withoutLine(c.input, s.text) : withLine(c.input, s.text) };
        });
        return true;
    };

    // The person starts a chat staged for steering with `text`, the
    // composer's lines as they left them: the staging is put away, the
    // surface's start runs, and the prompt that goes out is returned (its
    // own, else the composer's text, else the staging's empty prompt).
    const begin = (ref, text = "") => {
        const chat = find(ref);
        if (!chat?.steering) return null;
        const onStart = starts.get(chat.id);
        starts.delete(chat.id);
        const empty = chat.steering.emptyPrompt;
        updateChat(chat.id, (c) => (c.steering ? { ...c, steering: null } : c));
        let prompt = null;
        try { prompt = typeof onStart === "function" ? onStart(String(text || "")) : null; } catch { prompt = null; }
        return String(prompt || text || empty || "").trim();
    };

    // The person's own request, made somewhere else on the page (a button in
    // the Investigate tab), said in an existing chat as their message: the
    // chat comes on screen and the prompt goes through the console's own
    // submit, so a chat at work takes it the way it takes anything typed
    // while it works. False when the chat is gone.
    const say = (ref, prompt) => {
        const chat = find(ref);
        if (!chat || typeof sendPrompt !== "function") return false;
        surfaceChat(chat.id);
        Promise.resolve(ensureAgentFor(chat.id))
            .then((agent) => (agent ? sendPrompt(chat.id, String(prompt || "")) : null))
            .catch(() => {});
        return true;
    };

    return {
        stage,
        ask: typeof sendPrompt === "function" ? ask : undefined,
        offer,
        open,
        current: () => {
            const c = current();
            return c && c.chatId != null ? { chatId: c.chatId, serverChatId: c.serverChatId ?? null, busy: !!c.busy } : null;
        },
        equip: typeof rebuildAgent === "function" ? equip : undefined,
        say: typeof sendPrompt === "function" ? say : undefined,
        steer,
        toggleSteer,
        begin,
        notify: typeof runTurn === "function" ? notify : undefined,
        has: (ref) => Boolean(find(ref)),
        optionsFor,
        forget: (id) => { options.delete(id); starts.delete(id); },
        // A history wipe hands out low chat ids again; nothing may linger for them.
        clear: () => { options.clear(); starts.clear(); },
    };
}
